<template>
  <div class="rf-field rf-datepicker" :class="computedClass">

    <!-- Slot for custom label markup -->
    <slot>
      <!-- Purposefully ommitting 'for' attribute, since input is hidden -->
      <label class="rf-label">{{ label }}</label>
    </slot>

    <section class="rf-month" :class="{ 'show-focus-style': showFocusStyle }" @keydown.tab="showFocusStyle = true" @mousedown="showFocusStyle = false">
      <header ref="focus" tabindex="0">
        <a class="rf-prev" @click="previousMonth()" :class="{'disabled': previousMonthDisabled() || disabled }" :tabindex="previousMonthDisabled() || disabled ? -1 : 0" @keydown.space="previousMonth()">
          <icon name="caret-left" class="rf-icon"></icon>
          {{ previousMonthName }}
        </a>
        <span class="rf-month-name">{{ activeMonthName }} {{ activeDate.getFullYear() }}</span>
        <a class="rf-next" @click="nextMonth()" :class="{'disabled': nextMonthDisabled() || disabled }" :tabindex="nextMonthDisabled() || disabled ? -1 : 0" @keydown.space="nextMonth()">
          {{ nextMonthName }}
          <icon name="caret-right" class="rf-icon"></icon>
        </a>
      </header>
      <div class="rf-days-header">
        <span class="rf-day-header rf-cell" v-for="d in daysOfWeek">{{ d }}</span>
      </div>
      <!-- This markup inspired by https://www.iandevlin.com/blog/2015/07/html5/building-a-semantic-calendar-in-html5 -->
      <time class="rf-days" :datetime="getDatetime(activeDate).month" >
        <time :datetime="getDatetime(week[0].date).week" class="rf-week" v-for="week, i in days">
          <!-- Extra comments for inline block white-space issues -->
          <span class="rf-cell rf-blank" v-if="i === 0" v-for="n in (7 - week.length)">&nbsp;</span><!--
          --><time :datetime="getDatetime(day.date).day" class="rf-day rf-cell" :class="{ 'today': day.isToday, 'selected': day.isSelected, 'disabled': day.isDisabled }" v-for="day in week" @click="selectDate(day, $event)" @keydown.space="selectDate(day)" :tabindex="day.isDisabled ? -1 : 0"><span>{{ day.date.getDate() }}</span></time>
        </time>
      </time>
      <!-- Selected Date -->
      <div class="rf-selected">
        <transition name="fade-fast-in">
          <span v-if="value" key="selected">Selected: <strong>{{ formattedValue }}</strong> <small v-if="todaySelected">(today)</small></span>
          <span v-else key="placeholder"><em>Please choose a date</em></span>
        </transition>
        <!-- Icons -->
        <transition name="fade-fast">
          <icon name="exclamation-triangle" class="rf-icon rf-icon-invalid" v-if="showInvalid" key="invalid"></icon>
          <icon name="check" class="rf-icon rf-icon-valid" v-else-if="showValid" key="valid"></icon>
        </transition>
      </div>
      <!-- Errors -->
      <rf-message v-if="showInvalid">{{ errors.first(name) }}</rf-message>
    </section>
    <input type="hidden" v-bind="{ name, id: name, disabled }" v-model="value" v-validate="validation" ref="input">
  </div>
</template>

<script>
/**
 * Heavily adapted from https://github.com/charliekassel/vuejs-datepicker
 */

import mixins from '@/mixins/index'
import _d from '@/util/date'

export default {
  name: 'RfDatepicker',
  mixins: mixins,
  props: {
    disabledDates: {
      type: Object
    },
    onSelect: {
      type: Function
    }
  },

  // ////////////////////////////////////////////////////
  // Data
  // ////////////////////////////////////////////////////

  data () {
    return {
      // Remember, Vue cannot observe changes in a Date object, so we store the currently viewed month as a timestamp
      activeDateTimestamp: new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime(),
      monthNames: _d.local.monthNames,
      daysOfWeek: _d.local.daysOfWeek,
      fullDaysOfWeek: _d.local.fullDaysOfWeek,
      fullMonthNames: _d.local.fullMonthNames,
      showFocusStyle: false
    }
  },

  // ////////////////////////////////////////////////////
  // Computed
  // ////////////////////////////////////////////////////

  computed: {
    activeDate () {
      return new Date(this.activeDateTimestamp)
    },

    activeMonthName () {
      return this.monthNames[this.activeDate.getMonth()]
    },

    previousMonthName () {
      let d = new Date(this.activeDate.getFullYear(), this.activeDate.getMonth(), 0)
      d.setDate(1)
      return this.monthNames[d.getMonth()]
    },

    nextMonthName () {
      let d = new Date(this.activeDate.getFullYear(), this.activeDate.getMonth(), 1)
      d.setDate(_d.getDaysInMonth(d) + 1)
      return this.monthNames[d.getMonth()]
    },

    todaySelected () {
      return this.value && this.value.toDateString() === new Date().toDateString()
    },

    activeYear () {
      return this.activeDate.getFullYear()
    },

    formattedValue () {
      if (!this.value) return

      let day = this.value.getDate()

      return `${this.fullDaysOfWeek[this.value.getDay()]}, ${this.fullMonthNames[this.value.getMonth()]} ${day}${_d.getNthSuffix(day)}, ${this.value.getFullYear()}`
    },

    /**
     * This is a sort of configuration for the active month. It's formatted like:
     * [
     *    [Day, Day, Day],
     *    [Day, Day, Day, Day, Day, Day, Day],
     *    [Day, Day, Day, Day, Day, Day, Day],
     *    [Day, Day, Day, Day, Day, Day, Day],
     *    [Day, Day]
     * ]
     * where each 'row' represents an array of the days in that week. I can use this
     * to render the current month in a very straightforward way.
     * @return  {array}
     */
    days () {
      var month = []
      var week = []
      var d = 1
      var date = new Date(this.activeDateTimestamp)
      date.setDate(1)
      // We'll chunk our array of days in to groups of 7 (weeks) but we need to know
      // how to offset the chunks based on what day of the week is the first month
      var weekOffset = 7 - date.getDay()
      var daysInMonth = _d.getDaysInMonth(date)

      while (d <= daysInMonth) {
        week.push(this._makeDay(date, d))
        // Push week if we've accumulated 7 days OR if this is last day of the month
        if ((d - (weekOffset)) % 7 === 0 || d === daysInMonth) {
          month.push(week)
          // Reset week
          week = []
        }
        d++
      }

      return month
    }
  },

  // ////////////////////////////////////////////////////
  // Methods
  // ////////////////////////////////////////////////////

  methods: {

    /**
     * @param {Day} day - the day configuration object
     */
    selectDate (day, e) {
      if (day.isDisabled || this.disabled) {
        return false
      }
      // e.target.blur()
      this.value = day.date
      this.isDirty = true
      if (typeof this.onSelect === 'function') {
        this.onSelect(day.date)
      }
      this.$nextTick(function () {
        this.validate()
      })
    },

    /**
     * Create a day object
     * @param   {Date}  date
     * @param   {Number}  day - the day of the month (e.g. getDate())
     * @return  {Day}
     */
    _makeDay (date, day) {
      let dateObj = new Date(date.getFullYear(), date.getMonth(), day)
      return {
        date: dateObj,
        timestamp: dateObj.getTime(),
        isToday: dateObj.toDateString() === (new Date()).toDateString(),
        isSelected: this.isSelectedDate(dateObj),
        isDisabled: this.isDisabledDate(dateObj)
      }
    },

    /**
     * @typedef {Object} Day
     * @property {Date} date - the date object
     * @property {string} timestamp - the date represented as a timestamp.
     * @property {boolean} isToday - this this the current day
     * @property {boolean} isSelected - is this day currently selected
     * @property {boolean} isDisabled - is this day disabled per the disabledDates prop
     */

    /**
     * Get string values we can use for the 'datetime' attribute on our <time>
     * elements
     * @param   {Date}  date
     * @return  {DatetimeData}
     */
    getDatetime (date) {
      return {
        month: this.activeYear + '-' + _d.getPaddedMonth(date),
        week: this.activeYear + '-W' + _d.getWeekNumber(date),
        day: this.activeYear + '-' +
          _d.getPaddedMonth(date) + '-' +
          _d.getPaddedDay(date)
      }
    },

    /**
     * @typedef {Object} DatetimeData
     * @property {string} month - e.g. 2017-05
     * @property {string} week - e.g. 2017-W34
     * @property {string} day - e.g. 2017-05-16
     */

    /**
     * Programatically select a date
     * @param  {Date}  date
     */
    setDate (date) {
      let day = this._makeDay(date, date.getDate())
      if (day.isDisabled) {
        this.$warn(`Cannot set disabled date ${date.toDateString()}`)
        return
      }
      this.selectDate(day)
    },

    /**
     * Activate the next month on the datepicker
     */
    nextMonth () {
      if (this.nextMonthDisabled()) return false

      let d = this.activeDate
      d.setDate(d.getDate() + _d.getDaysInMonth(d))
      this.activeDateTimestamp = d.getTime()
    },

    /**
     * Activate the previous month on the datepicker
     */
    previousMonth () {
      if (this.previousMonthDisabled()) return false

      let d = new Date(this.activeDateTimestamp)
      d.setMonth(d.getMonth() - 1)
      this.activeDateTimestamp = d.getTime()
    },

    /**
     * Updates the calendar UI to show the currently selected date
     */
    activateSelectedDate () {
      if (this.value && this.value.getTime()) {
        this.activeDateTimestamp = this.value.getTime()
      } else {
        this.$warn('No selected date to activate')
      }
    },

    /**
     * Whether a day is disabled
     * @param {Date}
     * @return {Boolean}
     */
    isDisabledDate (date) {
      let disabled = false

      if (typeof this.disabledDates === 'undefined') {
        return false
      }

      if (typeof this.disabledDates.dates !== 'undefined') {
        this.disabledDates.dates.forEach((d) => {
          if (date.toDateString() === d.toDateString()) {
            disabled = true
            return true
          }
        })
      }
      if (typeof this.disabledDates.to !== 'undefined' && this.disabledDates.to && date < this.disabledDates.to) {
        disabled = true
      }
      if (typeof this.disabledDates.from !== 'undefined' && this.disabledDates.from && date >= this.disabledDates.from) {
        disabled = true
      }
      if (typeof this.disabledDates.days !== 'undefined' && this.disabledDates.days.indexOf(date.getDay()) !== -1) {
        disabled = true
      }
      if (typeof this.disabledDates.custom === 'function' && this.disabledDates.custom(date)) {
        disabled = true
      }
      return disabled
    },

    /**
     * Whether a day is selected
     * @param {Date}
     * @return {boolean}
     */
    isSelectedDate (date) {
      return this.value && this.value.toDateString() === date.toDateString()
    },

    /**
     * Whether the previous month is disabled
     * @return  {boolean}
     */
    previousMonthDisabled () {
      let selectedDate = new Date(this.activeDateTimestamp)
      let now = new Date()
      let currentDateIsInPreviousMonth = now.getMonth() < selectedDate.getMonth() && now.getFullYear() <= selectedDate.getFullYear()

      if (currentDateIsInPreviousMonth || typeof this.disabledDates === 'undefined' || typeof this.disabledDates.to === 'undefined' || !this.disabledDates.to) {
        return false
      }

      if (
        this.disabledDates.to.getMonth() >= selectedDate.getMonth() &&
        this.disabledDates.to.getFullYear() >= selectedDate.getFullYear()
      ) {
        return true
      }
      return false
    },

    /**
     * Whether the next month is disabled
     * @return  {boolean}
     */
    nextMonthDisabled () {
      if (typeof this.disabledDates === 'undefined' || typeof this.disabledDates.from === 'undefined' || !this.disabledDates.from) {
        return false
      }
      let d = new Date(this.activeDateTimestamp)
      if (
        this.disabledDates.from.getMonth() <= d.getMonth() &&
        this.disabledDates.from.getFullYear() <= d.getFullYear()
      ) {
        return true
      }
      return false
    },

    focus () {
      this.showFocusStyle = false
      this.$refs.focus.focus()
    }
  }
}
</script>



// WEBPACK FOOTER //
// RfDatepicker.vue?4a58d94f